import { createContext, useCallback, useContext, useMemo, useState } from 'react';
import { FormData, FormField } from '../../Types/forms';

type FormValues = Record<string, string>;

type FormContextValue = {
	form: FormData;
	values: FormValues;
	errors: Record<string, string | null>;
	touched: Record<string, boolean>;
	hiddenFields: Set<string>;
	disabled: boolean;
	setValue: (fieldKey: string, value: string) => void;
	setTouched: (fieldKey: string) => void;
	validate: () => boolean;
};

const FormContext = createContext<FormContextValue | null>(null);

export const useFormContext = () => {
	const ctx = useContext(FormContext);
	if (!ctx) throw new Error('useFormContext must be used within FormContextProvider');
	return ctx;
};

type FormContextProviderProps = {
	form: FormData;
	disabled?: boolean;
	children: React.ReactNode;
};

// ---------------------------------------------------------------------------
// Initial values
// ---------------------------------------------------------------------------

function buildInitialValues(form: FormData): FormValues {
	const values: FormValues = {};
	for (const field of form.fields) {
		switch (field.typeKey) {
			case 'checkboxlist': {
				const preselected = field.options.filter((o) => o.settings.preselected);
				values[field.key] = preselected.map((o) => String(o.id)).join(',');
				break;
			}
			case 'radiobuttonlist':
			case 'dropdownlist': {
				const preselected = field.options.find((o) => o.settings.preselected);
				values[field.key] = preselected ? String(preselected.id) : '';
				break;
			}
			case 'slider':
				values[field.key] = String(field.settings.min ?? 0);
				break;
			default:
				values[field.key] = '';
		}
	}
	return values;
}

// ---------------------------------------------------------------------------
// Hidden field computation (field listeners)
// ---------------------------------------------------------------------------

function computeHiddenFields(fields: FormField[], values: FormValues): Set<string> {
	const hidden = new Set<string>();
	for (const field of fields) {
		for (const listener of field.listeners) {
			const watchedValue = values[listener.key] ?? '';
			let matches = false;
			try {
				matches = new RegExp(listener.value).test(watchedValue);
			} catch {
				// Invalid regex — treat as no match
			}
			if (listener.action === 'show' && !matches) {
				hidden.add(field.key);
			} else if (listener.action === 'hide' && matches) {
				hidden.add(field.key);
			}
		}
	}
	return hidden;
}

// ---------------------------------------------------------------------------
// Validation
// ---------------------------------------------------------------------------

function computeErrors(fields: FormField[], values: FormValues, hiddenFields: Set<string>): Record<string, string | null> {
	const errors: Record<string, string | null> = {};
	for (const field of fields) {
		if (hiddenFields.has(field.key)) continue;
		// Non-input fields (label, video) don't need validation
		if (field.typeKey === 'label' || field.typeKey === 'video') continue;

		const value = values[field.key] ?? '';
		if (field.required && !value.trim()) {
			errors[field.key] = 'required';
		} else if (field.dataType === 'email' && value && !/^\S+@\S+\.\S+$/.test(value)) {
			errors[field.key] = 'email';
		} else {
			errors[field.key] = null;
		}
	}
	return errors;
}

// ---------------------------------------------------------------------------
// Provider
// ---------------------------------------------------------------------------

export const FormContextProvider = ({ form, disabled = false, children }: FormContextProviderProps) => {
	const [values, setValues] = useState<FormValues>(() => buildInitialValues(form));
	const [touched, setTouchedState] = useState<Record<string, boolean>>({});

	const hiddenFields = useMemo(() => computeHiddenFields(form.fields, values), [form.fields, values]);
	const errors = useMemo(() => computeErrors(form.fields, values, hiddenFields), [form.fields, values, hiddenFields]);

	const setValue = useCallback((fieldKey: string, value: string) => {
		setValues((prev) => ({ ...prev, [fieldKey]: value }));
	}, []);

	const setTouched = useCallback((fieldKey: string) => {
		setTouchedState((prev) => (prev[fieldKey] ? prev : { ...prev, [fieldKey]: true }));
	}, []);

	const validate = useCallback(() => {
		const newTouched: Record<string, boolean> = {};
		for (const field of form.fields) {
			if (!hiddenFields.has(field.key)) {
				newTouched[field.key] = true;
			}
		}
		setTouchedState(newTouched);
		return !form.fields.some((f) => !hiddenFields.has(f.key) && errors[f.key]);
	}, [form.fields, hiddenFields, errors]);

	const contextValue = useMemo<FormContextValue>(
		() => ({ form, values, errors, touched, hiddenFields, disabled, setValue, setTouched, validate }),
		[form, values, errors, touched, hiddenFields, disabled, setValue, setTouched, validate]
	);

	return <FormContext.Provider value={contextValue}>{children}</FormContext.Provider>;
};
